/**
 * Which pages a seller sees, and when.
 *
 * A seller who is still three weeks from listing does not need a Closing tab,
 * and a Home Activity page showing zeros reads as a broken product rather than
 * an empty one. Each page declares the stages it belongs to; the nav filters on
 * it and the routes enforce it, so a bookmarked URL can't reach a page that
 * doesn't apply yet.
 *
 * Stages, in order: pre_listing -> active -> under_contract -> closed.
 */

export const STAGES = ["pre_listing", "active", "under_contract", "closed"];

const ALL = STAGES;

/**
 * Path -> the stages where that page is meaningful.
 *
 * Anything not listed here is always available. Two deliberate calls:
 *  - Home Activity appears the day the listing goes Active and retires once
 *    they're Under Contract: showings stop, the numbers stop moving, and the
 *    page's job is done.
 *  - Closing is hidden until there's actually a deal to close.
 */
export const SELLER_PAGE_STAGES = {
  "/prep-guide": ["pre_listing", "active"],
  "/forms": ["pre_listing", "active"],
  "/activity": ["active"],
  "/closing": ["under_contract", "closed"],
};

/** Normalize whatever the API gave us into a known stage. */
export function normalizeStage(status) {
  const s = (status || "").trim();
  return STAGES.includes(s) ? s : "active";
}

/** Is this page available to a seller at this stage? */
export function isPageAvailable(path, status) {
  const allowed = SELLER_PAGE_STAGES[path];
  if (!allowed) return true;
  return allowed.includes(normalizeStage(status));
}

/** Filter a list of {to, label} nav links down to the current stage. */
export function filterLinksForStage(links, status) {
  return links.filter((l) => isPageAvailable(l.to, status));
}

/**
 * Why a page isn't showing yet — copy for the gate screen. Written for the
 * seller, so it explains rather than scolds.
 */
export function unavailableMessage(path, status) {
  const stage = normalizeStage(status);
  const before = (first) => STAGES.indexOf(stage) < STAGES.indexOf(first);
  switch (path) {
    case "/activity":
      return before("active")
        ? {
            title: "Not quite yet",
            body: "Once your home goes live, this page fills up with views, showings, and what buyers are saying about it.",
          }
        : {
            title: "You're past this one",
            body: "Home Activity tracked interest while your home was on the market. Now that you're under contract, your Important Dates and Closing pages are the ones to watch.",
          };
    case "/closing":
      return {
        title: "Nothing to close on yet",
        body: "This page opens up once you have an accepted offer. It'll walk you through everything between contract and keys.",
      };
    case "/prep-guide":
      return {
        title: "Prep is behind you",
        body: "Your home is already under contract — no more staging checklists needed.",
      };
    case "/forms":
      return {
        title: "Your forms are done",
        body: "Everything you filled out is with your agent. Nothing further is needed here.",
      };
    default:
      return {
        title: "Not available right now",
        body: "This page doesn't apply at your current stage.",
      };
  }
}
